import { create } from "zustand";
import { persist } from "zustand/middleware";

interface CheckoutStoreStateProps {
  variants: any[];
  addVariantGrid: (item: any) => void;
  addVariant: (item: any) => void;
  updateQuantity: (variantId: string, qty: number) => void;
  removeVariant: (variantId: string) => void;
  clearItems: () => void;
}

const parseNum = (v: any, fallback = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
};

export const useCheckoutStore = create<CheckoutStoreStateProps>()(
  persist(
    (set, get) => ({
      variants: [],

      // Upsert por 'variants'. Reemplaza qty (no suma) y respeta available.
      addVariantGrid: (item: any) => {
        const list = get().variants;
        const idx = list.findIndex((v: any) => v.variants === item.variants);
        const current = idx >= 0 ? list[idx] : undefined;

        // available persistido tiene prioridad; si no existe, usa el entrante.
        const persistedAvailable = current?.available;
        const incomingAvailable = item?.available;
        const effectiveAvailable =
          persistedAvailable !== undefined
            ? parseNum(persistedAvailable, Infinity)
            : incomingAvailable !== undefined
            ? parseNum(incomingAvailable, Infinity)
            : Infinity;

        // cantidad entrante (qty o quantity)
        const incomingQty = parseNum(item?.qty ?? item?.quantity ?? 0, 0);
        const clampedQty = Math.max(
          0,
          Math.min(
            incomingQty,
            Number.isFinite(effectiveAvailable)
              ? effectiveAvailable
              : incomingQty
          )
        );

        if (idx >= 0) {
          // actualizar línea existente
          const updated = [...list];
          const nextLine = {
            ...current,
            ...item,
            qty: clampedQty,
            // solo sobrescribe available si vino definido en item
            ...(incomingAvailable !== undefined
              ? { available: parseNum(incomingAvailable, 0) }
              : {}),
          };

          if (clampedQty === 0) {
            updated.splice(idx, 1);
          } else {
            updated[idx] = nextLine;
          }

          set({ variants: updated });
          return;
        }

        // nueva línea (no guardar si qty 0)
        if (clampedQty === 0) return;

        const newLine = {
          ...item,
          qty: clampedQty,
          ...(incomingAvailable !== undefined
            ? { available: parseNum(incomingAvailable, 0) }
            : {}),
        };

        set({ variants: [...list, newLine] });
      },

       addVariant: (item: any) => {
        const variants = get().variants;
        const index = variants.findIndex((v) => v.variants === item.variants);

        const parseNum = (v: any) => {
          const n = Number(v);
          return Number.isFinite(n) ? n : 0;
        };

        const newQty = parseNum(item.qty ?? item.quantity ?? 1);

        if (index !== -1) {
          const updated = [...variants];
          const current = updated[index];
          const currentQty = parseNum(current.qty ?? current.quantity ?? 1);
          // usa el available guardado; si no existe, usa el del item
          const available = parseNum(current.available ?? item.available);

          // si excede, NO agrega ni incrementa
          if (currentQty + newQty > available) {
            console.warn("Cantidad excede la disponibilidad");
            return;
          }

          updated[index] = {
            ...current,
            qty: currentQty + newQty,
            available, // asegúrate de mantenerlo
          };

          set({ variants: updated });
        } else {
          const available = parseNum(item.available);

          if (newQty > available) {
            console.warn("Cantidad excede la disponibilidad");
            return;
          }

          set({
            variants: [...variants, { ...item, qty: newQty, available }],
          });
        }
      },

      // Reemplaza qty para el variantId dado, con clamp y borrado si queda en 0.
      updateQuantity: (variantId: string, qty: number) => {
        const list = get().variants;
        const idx = list.findIndex((v: any) => v.variants === variantId);
        if (idx === -1) return;

        const current = list[idx];
        const available = parseNum(current?.available, Infinity);
        const clampedQty = Math.max(
          0,
          Math.min(
            parseNum(qty, 0),
            Number.isFinite(available) ? available : parseNum(qty, 0)
          )
        );

        const updated = [...list];
        if (clampedQty === 0) {
          updated.splice(idx, 1);
        } else {
          updated[idx] = { ...current, qty: clampedQty };
        }
        set({ variants: updated });
      },

      removeVariant: (variantId: string) => {
        set({
          variants: get().variants.filter((v: any) => v.variants !== variantId),
        });
      },

      clearItems: () => set({ variants: [] }),
    }),
    { name: "checkout-storage" }
  )
);
